/**
 * WebSocket hooks for real-time signal, price, and trade streaming.
 *
 * `useWebSocket(path)` — low-level hook that connects to a backend WS
 *   endpoint with auto-reconnect + exponential backoff.
 *
 * `useSignalStream()` — high-level hook combining /ws/signals with the
 *   TanStack Query cache so REST queries stay in sync with live pushes.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getWsBaseUrl } from "@/lib/api-client";
import { TRADING_QUERY_KEYS } from "@/sdk/hooks/use-trading";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SocketState = "disconnected" | "connecting" | "connected" | "reconnecting";

interface WsEnvelope<T = Record<string, unknown>> {
  type: string;
  data: T;
  timestamp: string;
}

interface UseWebSocketOptions {
  /** Disable the connection entirely (e.g. when the tab is hidden). */
  enabled?: boolean;
  /** Maximum reconnect attempts before giving up (0 = unlimited). */
  maxReconnectAttempts?: number;
  /** Initial backoff delay in ms. */
  baseReconnectDelayMs?: number;
  /** Ceiling for the exponential backoff. */
  maxReconnectDelayMs?: number;
  /** Auth token passed as ?token= query param. */
  token?: string;
  /** Cap the number of messages kept in the `messages` array. */
  maxMessages?: number;
}

interface UseWebSocketReturn<T> {
  state: SocketState;
  lastMessage: WsEnvelope<T> | null;
  messages: WsEnvelope<T>[];
  reconnectExhausted: boolean;
  reconnect: () => void;
  send: (data: Record<string, unknown>) => void;
}

// ---------------------------------------------------------------------------
// useWebSocket — generic WS hook with auto-reconnect
// ---------------------------------------------------------------------------

export function useWebSocket<T = Record<string, unknown>>(
  path: string,
  options?: UseWebSocketOptions,
): UseWebSocketReturn<T> {
  const {
    enabled = true,
    maxReconnectAttempts = 10,
    baseReconnectDelayMs = 1000,
    maxReconnectDelayMs = 30000,
    token,
    maxMessages = 50,
  } = options ?? {};

  const [state, setState] = useState<SocketState>("disconnected");
  const [lastMessage, setLastMessage] = useState<WsEnvelope<T> | null>(null);
  const [messages, setMessages] = useState<WsEnvelope<T>[]>([]);
  const [reconnectExhausted, setReconnectExhausted] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);

  // Live refs so callbacks always see fresh values without re-creating
  const enabledRef = useRef(enabled);
  const maxMessagesRef = useRef(maxMessages);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    maxMessagesRef.current = maxMessages;
  }, [maxMessages]);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimeoutRef.current !== null) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    clearReconnectTimer();
    if (wsRef.current) {
      wsRef.current.close(1000, "manual_disconnect");
      wsRef.current = null;
    }
    reconnectAttemptRef.current = 0;
    setReconnectExhausted(false);
    setState("disconnected");
  }, [clearReconnectTimer]);

  const connect = useCallback(() => {
    if (!enabledRef.current || wsRef.current) return;

    let url = `${getWsBaseUrl()}${path}`;
    if (token) {
      url += `${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
    }

    setState(reconnectAttemptRef.current > 0 ? "reconnecting" : "connecting");

    try {
      wsRef.current = new WebSocket(url);
    } catch {
      wsRef.current = null;
      setState("disconnected");
      return;
    }

    wsRef.current.onopen = () => {
      reconnectAttemptRef.current = 0;
      setReconnectExhausted(false);
      setState("connected");
    };

    wsRef.current.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data) as WsEnvelope<T>;
        // Skip heartbeats from the messages list
        if (parsed.type === "heartbeat") return;

        setLastMessage(parsed);
        setMessages((prev) => [parsed, ...prev].slice(0, maxMessagesRef.current));
      } catch {
        // Ignore malformed frames
      }
    };

    const thisSocket = wsRef.current;
    wsRef.current.onclose = () => {
      // Only process if this close event is for the CURRENT socket.
      // A new socket may have been created while this one was closing,
      // and we must not clobber it or trigger a spurious reconnect.
      if (wsRef.current !== thisSocket) return;

      wsRef.current = null;
      setState("disconnected");

      if (!enabledRef.current) return;

      reconnectAttemptRef.current += 1;
      if (maxReconnectAttempts > 0 && reconnectAttemptRef.current > maxReconnectAttempts) {
        setReconnectExhausted(true);
        return;
      }

      // Exponential backoff: 1s, 2s, 4s, 8s, ... capped at maxReconnectDelayMs
      const delay = Math.min(
        baseReconnectDelayMs * Math.pow(2, reconnectAttemptRef.current - 1),
        maxReconnectDelayMs,
      );

      setState("reconnecting");
      clearReconnectTimer();
      reconnectTimeoutRef.current = window.setTimeout(() => connect(), delay);
    };

    wsRef.current.onerror = () => {
      wsRef.current?.close();
    };
  }, [path, token, baseReconnectDelayMs, maxReconnectDelayMs, maxReconnectAttempts, clearReconnectTimer]);

  // Connect/disconnect when `enabled` changes
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }
    return () => disconnect();
  }, [enabled, connect, disconnect]);

  const send = useCallback((data: Record<string, unknown>) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  return {
    state,
    lastMessage,
    messages,
    reconnectExhausted,
    reconnect: () => {
      disconnect();
      setTimeout(() => connect(), 50);
    },
    send,
  };
}

// ---------------------------------------------------------------------------
// useSignalStream — /ws/signals with TanStack Query cache integration
// ---------------------------------------------------------------------------

/**
 * Connects to /ws/signals and invalidates related query keys whenever
 * a new signal arrives so any useQuery hooks that display signals re-fetch
 * transparently.
 */
export function useSignalStream(options?: { enabled?: boolean; token?: string }) {
  const queryClient = useQueryClient();

  const ws = useWebSocket("/ws/signals", {
    enabled: options?.enabled ?? true,
    token: options?.token,
  });

  // Invalidate relevant query caches when a signal arrives
  const prevMessageRef = useRef(ws.lastMessage);

  useEffect(() => {
    if (ws.lastMessage && ws.lastMessage !== prevMessageRef.current) {
      prevMessageRef.current = ws.lastMessage;

      if (ws.lastMessage.type === "signal") {
        // Invalidate signal-related queries so they re-fetch from REST
        queryClient.invalidateQueries({ queryKey: ["signals"] });
        queryClient.invalidateQueries({ queryKey: ["alpha-signals"] });
        queryClient.invalidateQueries({ queryKey: ["radar"] });
        // Keys the mounted views ACTUALLY use: the dashboard signal cards and
        // the alpha radar hooks — without these the bridge leaves the visible
        // panels to their polling intervals.
        queryClient.invalidateQueries({ queryKey: ["signals-summary"] });
        queryClient.invalidateQueries({ queryKey: ["signals-contrarian"] });
        queryClient.invalidateQueries({ queryKey: ["alpha", "radar"] });
      }
    }
  }, [ws.lastMessage, queryClient]);

  return ws;
}

/**
 * Connects to /ws/prices and invalidates market price queries.
 */
export function usePriceStream(options?: { enabled?: boolean; token?: string }) {
  const queryClient = useQueryClient();

  const ws = useWebSocket("/ws/prices", {
    enabled: options?.enabled ?? true,
    token: options?.token,
  });

  const prevMessageRef = useRef(ws.lastMessage);

  useEffect(() => {
    if (ws.lastMessage && ws.lastMessage !== prevMessageRef.current) {
      prevMessageRef.current = ws.lastMessage;

      if (ws.lastMessage.type === "price") {
        queryClient.invalidateQueries({ queryKey: ["markets"] });
        queryClient.invalidateQueries({ queryKey: ["prices"] });
      }
    }
  }, [ws.lastMessage, queryClient]);

  return ws;
}

/**
 * Connects to /ws/trades and invalidates trade/position queries.
 */
export function useTradeStream(options?: { enabled?: boolean; token?: string }) {
  const queryClient = useQueryClient();

  const ws = useWebSocket("/ws/trades", {
    enabled: options?.enabled ?? true,
    token: options?.token,
  });

  const prevMessageRef = useRef(ws.lastMessage);

  useEffect(() => {
    if (ws.lastMessage && ws.lastMessage !== prevMessageRef.current) {
      prevMessageRef.current = ws.lastMessage;

      if (ws.lastMessage.type === "trade") {
        queryClient.invalidateQueries({ queryKey: ["trades"] });
        queryClient.invalidateQueries({ queryKey: ["positions"] });
        queryClient.invalidateQueries({ queryKey: ["portfolio"] });
        queryClient.invalidateQueries({ queryKey: TRADING_QUERY_KEYS.positions });
        queryClient.invalidateQueries({ queryKey: TRADING_QUERY_KEYS.portfolio });
        queryClient.invalidateQueries({ queryKey: TRADING_QUERY_KEYS.livePositions });
        queryClient.invalidateQueries({ queryKey: TRADING_QUERY_KEYS.liveOpenOrders });
        queryClient.invalidateQueries({ queryKey: TRADING_QUERY_KEYS.balance });
        // Paper open/close events stream here too (paper_trading.py emits) —
        // refresh the paper views the operator is actually watching instead
        // of leaving them to the 30s poll.
        queryClient.invalidateQueries({ queryKey: ["paper-positions-open"] });
        queryClient.invalidateQueries({ queryKey: ["paper-portfolio-card"] });
      }
    }
  }, [ws.lastMessage, queryClient]);

  return ws;
}
